import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCreateBusinessUnit, useUpdateBusinessUnit } from '@/hooks/useBusinessUnits';
import type { BusinessUnit, BusinessUnitInput } from '@/services/businessUnitService';

// Limits mirror the API's CreateBusinessUnitRequest / UpdateBusinessUnitRequest validation
const schema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(100, 'Use at most 100 characters'),
  code: z.string().trim().min(2, 'Use at least 2 characters').max(10, 'Use at most 10 characters'),
  businessUnitKey: z.string().trim().min(1, 'Enter the external key').max(100, 'Use at most 100 characters'),
  location: z.string().trim().min(1, 'Enter a location').max(200, 'Use at most 200 characters'),
  description: z.string().trim().max(500, 'Use at most 500 characters'),
});

type FormValues = BusinessUnitInput;

const EMPTY: FormValues = { name: '', code: '', businessUnitKey: '', location: '', description: '' };

const toValues = (bu: BusinessUnit): FormValues => ({
  name: bu.name,
  code: bu.code,
  businessUnitKey: bu.businessUnitKey ?? '',
  location: bu.location ?? '',
  description: bu.description ?? '',
});

interface BusinessUnitDialogProps {
  open: boolean;
  /** The unit being edited; omit to create a new one */
  businessUnit?: BusinessUnit | null;
  onOpenChange: (open: boolean) => void;
}

export function BusinessUnitDialog({ open, businessUnit, onOpenChange }: BusinessUnitDialogProps) {
  const isEdit = !!businessUnit;
  const createBusinessUnit = useCreateBusinessUnit();
  const updateBusinessUnit = useUpdateBusinessUnit();
  const isPending = createBusinessUnit.isPending || updateBusinessUnit.isPending;

  const form = useForm<FormValues>({ resolver: zodResolver(schema) as Resolver<FormValues>, defaultValues: EMPTY });

  // Reset whenever the dialog opens, so a previous edit never leaks into the next one
  useEffect(() => {
    if (open) form.reset(businessUnit ? toValues(businessUnit) : EMPTY);
  }, [open, businessUnit, form]);

  const onSubmit = (values: FormValues) => {
    const onSuccess = () => onOpenChange(false);
    // A duplicate code is the one conflict the user can fix right here
    const onError = (error: unknown) => {
      const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;
      if (response?.status === 409 && /code/i.test(response.data?.detail ?? '')) {
        form.setError('code', { message: 'This code is already in use' });
        form.setFocus('code');
      }
    };

    if (businessUnit) {
      updateBusinessUnit.mutate({ id: businessUnit.id, data: values }, { onSuccess, onError });
    } else {
      createBusinessUnit.mutate(values, { onSuccess, onError });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 bg-card p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pb-2 pt-6 text-left">
          <DialogTitle>{isEdit ? 'Edit business unit' : 'Add business unit'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Changes apply to every contract that uses this business unit.'
              : 'It becomes available when assigning volumes on contracts.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" autoFocus placeholder="e.g. De Heus Vietnam" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code</FormLabel>
                      <FormControl>
                        <Input autoComplete="off" placeholder="e.g. VN" {...field} />
                      </FormControl>
                      <FormDescription>2–10 characters, unique.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="businessUnitKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>External key</FormLabel>
                      <FormControl>
                        <Input autoComplete="off" placeholder="e.g. BU-1042" {...field} />
                      </FormControl>
                      <FormDescription>Identifier in other systems.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" placeholder="City, country" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Description <span className="font-normal text-muted-foreground">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="gap-2 border-t px-6 py-4 sm:space-x-0">
              <Button type="button" variant="ghost" disabled={isPending} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? 'Save changes' : 'Add business unit'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
